import { migrateLegacyInvoitStorageKeys } from '../config/storageKeys';

/**
 * Secure Storage Utility
 * Encrypts sensitive data before storing in localStorage
 * Prevents exposure of license keys, credentials, and user data
 */

/**
 * Simple XOR cipher for lightweight encryption
 * Note: This is NOT cryptographically secure, but provides basic obfuscation
 * For production, consider using Web Crypto API with a proper key derivation
 * @param {string} data - Data to encrypt/decrypt
 * @param {string} key - Encryption key
 * @returns {string} Encrypted/decrypted data
 */
const xorCipher = (data, key) => {
  let result = '';
  for (let i = 0; i < data.length; i++) {
    result += String.fromCharCode(data.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return result;
};

/**
 * Generate a device-specific encryption key
 * Uses browser fingerprinting for consistency across sessions
 * @returns {string} Encryption key
 */
const getDeviceKey = () => {
  // Create a fingerprint from available browser data
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    new Date().getTimezoneOffset(),
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    'qawaem-erp-v1' // App-specific salt
  ].join('|');
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Convert to hex string
  return Math.abs(hash).toString(16).padStart(8, '0');
};

/**
 * Encrypt data using Base64 + XOR cipher
 * @param {string} data - Data to encrypt
 * @param {string} customKey - Optional custom encryption key
 * @returns {string} Encrypted data
 */
export const encrypt = (data, customKey = null) => {
  if (typeof data !== 'string') {
    data = JSON.stringify(data);
  }
  
  const key = customKey || getDeviceKey();
  
  try {
    // Step 1: XOR cipher
    const encrypted = xorCipher(data, key);
    
    // Step 2: Base64 encode
    const base64 = btoa(unescape(encodeURIComponent(encrypted)));
    
    // Step 3: Add prefix to identify encrypted data
    return `ENC_V1:${base64}`;
  } catch (error) {
    console.error('Encryption error:', error);
    return data; // Return original on error (fallback)
  }
};

/**
 * Decrypt data
 * @param {string} encryptedData - Encrypted data
 * @param {string} customKey - Optional custom encryption key
 * @returns {string} Decrypted data
 */
export const decrypt = (encryptedData, customKey = null) => {
  if (!encryptedData || typeof encryptedData !== 'string') {
    return encryptedData;
  }
  
  // Check if data is encrypted
  if (!encryptedData.startsWith('ENC_V1:')) {
    return encryptedData; // Not encrypted, return as-is
  }
  
  const key = customKey || getDeviceKey();
  
  try {
    // Remove prefix
    const base64 = encryptedData.substring(7);
    
    // Step 1: Base64 decode
    const encrypted = decodeURIComponent(escape(atob(base64)));
    
    // Step 2: XOR decipher
    const decrypted = xorCipher(encrypted, key);
    
    return decrypted;
  } catch (error) {
    console.error('Decryption error:', error);
    return null; // Return null on error
  }
};

/**
 * Securely set item in localStorage with encryption
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 * @param {boolean} shouldEncrypt - Whether to encrypt the value (default: true)
 * @returns {boolean} Success status
 */
export const secureSetItem = (key, value, shouldEncrypt = true) => {
  try {
    // Convert to string if needed
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    
    // Encrypt if requested
    const finalValue = shouldEncrypt ? encrypt(stringValue) : stringValue;
    
    // Store in localStorage
    localStorage.setItem(key, finalValue);
    
    return true;
  } catch (error) {
    console.error(`Failed to securely set ${key}:`, error);
    return false;
  }
};

/**
 * Securely get item from localStorage with decryption
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if key doesn't exist
 * @param {boolean} shouldDecrypt - Whether to decrypt the value (default: true)
 * @returns {any} Retrieved value or default
 */
export const secureGetItem = (key, defaultValue = null, shouldDecrypt = true) => {
  try {
    const value = localStorage.getItem(key);
    
    if (value === null || value === undefined) {
      return defaultValue;
    }
    
    // Decrypt if needed
    const decryptedValue = shouldDecrypt ? decrypt(value) : value;
    
    if (decryptedValue === null) {
      console.warn(`Failed to decrypt ${key}, returning default`);
      return defaultValue;
    }
    
    // Try to parse as JSON
    try {
      return JSON.parse(decryptedValue);
    } catch (e) {
      // Not JSON, return as string
      return decryptedValue;
    }
  } catch (error) {
    console.error(`Failed to securely get ${key}:`, error);
    return defaultValue;
  }
};

/**
 * Remove item from localStorage
 * @param {string} key - Storage key
 * @returns {boolean} Success status
 */
export const secureRemoveItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Failed to remove ${key}:`, error);
    return false;
  }
};

/**
 * Clear all items from localStorage
 * @returns {boolean} Success status
 */
export const secureClear = () => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Failed to clear localStorage:', error);
    return false;
  }
};

/**
 * Check if a value in localStorage is encrypted
 * @param {string} key - Storage key
 * @returns {boolean} True if encrypted
 */
export const isEncrypted = (key) => {
  try {
    const value = localStorage.getItem(key);
    return value !== null && value.startsWith('ENC_V1:');
  } catch (error) {
    return false;
  }
};

/**
 * Migrate existing unencrypted data to encrypted format
 * @param {string[]} keys - Array of keys to migrate
 * @returns {Object} Migration results {success: number, failed: number}
 */
export const migrateToEncrypted = (keys) => {
  let success = 0;
  let failed = 0;
  
  for (const key of keys) {
    try {
      // Check if already encrypted
      if (isEncrypted(key)) {
        console.log(`${key} is already encrypted, skipping`);
        success++;
        continue;
      }
      
      // Get current value
      const value = localStorage.getItem(key);
      
      if (value === null) {
        console.log(`${key} does not exist, skipping`);
        continue;
      }
      
      // Encrypt and store
      const encrypted = encrypt(value);
      localStorage.setItem(key, encrypted);
      
      console.log(`✅ Migrated ${key} to encrypted storage`);
      success++;
    } catch (error) {
      console.error(`❌ Failed to migrate ${key}:`, error);
      failed++;
    }
  }
  
  return { success, failed, total: keys.length };
};

/**
 * List of sensitive keys that should always be encrypted
 */
export const SENSITIVE_KEYS = [
  'qawaem_license_key',
  'qawaem_user',
  'qawaem_session',
  'qawaem_admin_temp',
  'qawaem_admin_backup',
  'qawaem_auth_token',
  'qawaem_api_key',
  'qawaem_bypass_login'
];

/**
 * Automatically encrypt all sensitive keys on app init
 * Call this once during app initialization
 * @returns {Object} Migration results
 */
export const autoMigrateSensitiveData = () => {
  console.log('🔒 Starting automatic migration of sensitive data...');
  const results = migrateToEncrypted(SENSITIVE_KEYS);
  console.log(`🔒 Migration complete: ${results.success}/${results.total} keys encrypted`);
  
  if (results.failed > 0) {
    console.warn(`⚠️ ${results.failed} keys failed to migrate`);
  }
  
  return results;
};

/**
 * Secure storage wrapper object
 * Drop-in replacement for localStorage with automatic encryption
 */
export const secureStorage = {
  setItem: (key, value) => secureSetItem(key, value, SENSITIVE_KEYS.includes(key)),
  getItem: (key, defaultValue = null) => secureGetItem(key, defaultValue, SENSITIVE_KEYS.includes(key)),
  removeItem: secureRemoveItem,
  clear: secureClear,
  
  // Direct encryption methods
  encrypt,
  decrypt,
  
  // Utility methods
  isEncrypted,
  migrateToEncrypted,
  autoMigrateSensitiveData
};

/**
 * Initialize secure storage
 * Call this once when the app starts
 */
export const initSecureStorage = () => {
  console.log('🔒 Initializing secure storage...');

  try {
    migrateLegacyInvoitStorageKeys();
    // Auto-migrate sensitive data
    const results = autoMigrateSensitiveData();
    
    // Log encryption status
    console.log('🔒 Secure storage initialized successfully');
    console.log(`🔐 Device key: ${getDeviceKey().substring(0, 4)}****`);
    console.log(`📦 Encrypted keys: ${results.success}/${results.total}`);
    
    return true;
  } catch (error) {
    console.error('❌ Failed to initialize secure storage:', error);
    return false;
  }
};

export default secureStorage;

